import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtMoney, fmtDate, fmtNumber, fmtDateRange, cycleShort } from "@/lib/fmt";
import { FileText, Info, Download } from "lucide-react";
import { toast } from "sonner";

export default function Statements() {
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(null);
  useEffect(() => { api.get("/me/statements").then(({ data }) => setData(data)); }, []);

  const onDownload = async (cycle) => {
    setDownloading(cycle);
    try {
      const resp = await api.get(`/me/statements/${cycle}.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `HHP_Statement_${cycle}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Couldn't download statement — please try again.");
    } finally {
      setDownloading(null);
    }
  };

  if (!data) return <div className="text-hh-ink2">Loading statements…</div>;
  const { current_cycle: cc, last_updated: lastUpdated } = data;
  // Belt-and-braces filter: only show quarterly cycles (Q1-YYYY..Q4-YYYY).
  // Legacy half-year records (H1/H2, "First half", "Second half") are hidden.
  const payments = (data.payments || []).filter((p) => /^Q[1-4]-\d{4}$/.test(p.cycle || ""));

  const lastUpdatedLabel = (() => {
    if (!lastUpdated) return null;
    try {
      const d = new Date(lastUpdated);
      return d.toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });
    } catch (_e) { return null; }
  })();

  const currencies = Array.from(new Set([
    ...Object.keys(cc.total_compensation || {}),
    ...Object.keys(cc.royalties_by_currency || {}),
  ]));

  return (
    <div className="space-y-16" data-testid="statements-page">
      <header>
        <p className="hh-overline mb-3">Statements & Payments</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-hh-ink tracking-tight">
          Every payment, plainly recorded.
        </h1>
        <p className="mt-4 text-hh-ink2 max-w-2xl text-lg leading-relaxed">
          Royalties are paid quarterly — each quarter falls due at the end of the following quarter and is paid within 30 days (Jan–Mar by 31 July, Apr–Jun by 31 October, Jul–Sep by 31 January, Oct–Dec by 30 April). You can watch your current cycle accrue below.
        </p>
        {lastUpdatedLabel && (
          <p className="mt-4 inline-flex items-center gap-2 text-xs text-hh-ink2 bg-hh-muted/60 border border-hh-border rounded-full px-3 py-1.5" data-testid="statements-last-updated">
            <span className="h-1.5 w-1.5 rounded-full bg-hh-sage" />
            Sales data last refreshed <strong className="text-hh-ink font-medium">{lastUpdatedLabel}</strong>
          </p>
        )}
      </header>

      {/* Live current cycle */}
      <section data-testid="current-cycle-card">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
          <p className="hh-overline">Live · this cycle</p>
          <p className="text-xs text-hh-ink2 flex items-center gap-1.5">
            <Info size={12} strokeWidth={1.5} /> You receive {cc.royalty_pct ?? 95}% · Healing House retains {cc.publisher_pct ?? 5}%
          </p>
        </div>
        <p className="text-sm text-hh-ink2 mb-4 bg-hh-muted/60 border border-hh-border rounded-lg px-4 py-3 inline-block">
          Data is updated monthly from IngramSpark — not real-time.
          {cc.latest_period_on_file && (
            <> Latest period ingested: <span className="text-hh-ink font-medium">{(() => { const [y,m]=cc.latest_period_on_file.split("-").map(Number); return new Date(y,m-1,1).toLocaleString(undefined,{month:"short",year:"2-digit"}); })()}</span>.</>
          )}
        </p>

        <div className="hh-card p-8 md:p-10">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Kpi label="Cycle" value={cycleShort(cc.cycle)} sub={fmtDateRange(cc.start, cc.end)} />
            <Kpi label="Units sold" value={fmtNumber(cc.units)} testid="current-cycle-units" />
            <Kpi label="Next payment" value={cc.next_payment_date} accent />
            <Kpi label="Statement" value="Live · in progress" sub="Closes at cycle end" />
          </div>

          {currencies.length === 0 ? (
            <p className="text-sm text-hh-ink2">No sales recorded this cycle yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="current-cycle-breakdown">
                <thead className="text-left">
                  <tr className="border-b border-hh-border">
                    <th className="py-3 pr-6 hh-overline">Currency</th>
                    <th className="py-3 pr-6 hh-overline">Total compensation paid<br/><span className="text-[10px] text-hh-ink2 normal-case font-normal tracking-normal">(IngramSpark → Healing House)</span></th>
                    <th className="py-3 pr-6 hh-overline">Your share ({cc.royalty_pct}%)</th>
                    <th className="py-3 pr-6 hh-overline">Publisher ({cc.publisher_pct}%)</th>
                  </tr>
                </thead>
                <tbody>
                  {currencies.map((ccy) => (
                    <tr key={ccy} className="border-b border-hh-border last:border-0">
                      <td className="py-4 pr-6 font-semibold text-hh-ink">{ccy}</td>
                      <td className="py-4 pr-6 font-serif text-lg text-hh-ink">
                        {fmtMoney(cc.total_compensation?.[ccy] || 0, ccy)}
                      </td>
                      <td className="py-4 pr-6 font-serif text-lg text-hh-primary">
                        {fmtMoney(cc.royalties_by_currency?.[ccy] || 0, ccy)}
                      </td>
                      <td className="py-4 pr-6 font-serif text-lg text-hh-ink2">
                        {fmtMoney(cc.publisher_share?.[ccy] || 0, ccy)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* Past payments */}
      <section data-testid="past-payments-section">
        <p className="hh-overline mb-4">Past statements</p>

        <div className="mb-4 rounded-lg border border-hh-border bg-hh-muted/50 px-4 py-3 text-sm text-hh-ink2 space-y-1.5" data-testid="status-legend">
          <p><span className="inline-block h-2 w-2 rounded-full bg-hh-sage mr-1.5 align-middle"></span><strong className="text-hh-ink">Paid</strong> means the royalty has been transferred to your nominated bank account — the &lsquo;Paid on&rsquo; date is when funds left Healing House.</p>
          <p><span className="inline-block h-2 w-2 rounded-full bg-[#D89B00] mr-1.5 align-middle"></span><strong className="text-hh-ink">Pending</strong> means the royalty has been calculated from confirmed sales, but the transfer hasn&rsquo;t been made yet. Pending amounts are paid quarterly &mdash; each quarter falls due at the end of the following quarter and is paid within 30 days (e.g. April&ndash;June is paid by 31 October). Amounts may still adjust slightly if IngramSpark issues a restatement for the period.</p>
        </div>
        {payments.length === 0 ? (
          <div className="hh-card p-10 text-center">
            <FileText size={32} strokeWidth={1.2} className="text-hh-ink2 mx-auto mb-4" />
            <p className="font-serif text-xl text-hh-ink mb-2">No past statements yet</p>
            <p className="text-sm text-hh-ink2">
              Your first statement will land here after the {cc.cycle} cycle closes on {cc.end}.
            </p>
          </div>
        ) : (
          <div className="hh-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-hh-muted/60 border-b border-hh-border">
                <tr className="text-left">
                  <th className="px-6 py-4 hh-overline">Cycle</th>
                  <th className="px-6 py-4 hh-overline">Period</th>
                  <th className="px-6 py-4 hh-overline">Amount</th>
                  <th className="px-6 py-4 hh-overline">Status</th>
                  <th className="px-6 py-4 hh-overline">Paid on</th>
                  <th className="px-6 py-4 hh-overline text-right">Statement</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-b border-hh-border last:border-0" data-testid={`payment-row-${p.cycle}`}>
                    <td className="px-6 py-5 font-serif text-hh-ink">
                      {cycleShort(p.cycle)}
                    </td>
                    <td className="px-6 py-5 text-hh-ink2">{fmtDateRange(p.period_start, p.period_end)}</td>
                    <td className="px-6 py-5 font-medium text-hh-ink">{fmtMoney(p.amount, p.currency)}</td>
                    <td className="px-6 py-5">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs ${
                        p.status === "paid"
                          ? "bg-hh-sage/15 text-hh-sage"
                          : "bg-hh-accent/15 text-[#9F7300]"
                      }`}>
                        {p.status === "paid" ? "Paid" : "Pending"}
                      </span>
                    </td>
                    <td className="px-6 py-5 text-hh-ink2">{p.paid_date ? fmtDate(p.paid_date) : "—"}</td>
                    <td className="px-6 py-5 text-right">
                      <button
                        onClick={() => onDownload(p.cycle)}
                        disabled={downloading === p.cycle}
                        className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md bg-hh-primary/10 hover:bg-hh-primary/20 text-hh-primary font-medium disabled:opacity-50"
                        data-testid={`download-statement-${p.cycle}-${p.id}`}
                      >
                        <Download size={12} strokeWidth={2} />
                        {downloading === p.cycle ? "…" : "PDF"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Kpi({ label, value, sub, accent, testid }) {
  return (
    <div data-testid={testid}>
      <div className="hh-overline mb-2">{label}</div>
      <div className={`font-serif text-2xl ${accent ? "text-hh-primary" : "text-hh-ink"}`}>{value || "—"}</div>
      {sub && <div className="text-xs text-hh-ink2 mt-1">{sub}</div>}
    </div>
  );
}
